/* quiz-main.js - Adéorí Texture Match Quiz logic (v2 - behavioral, deterministic)
 *
 * Rebuild goal: the quiz DIAGNOSES. It never asks the customer to self-identify
 * their curl type by name or to pick between two finished textures ("which of
 * these two is your hair?"). Every question is a first-person OBSERVATION about
 * their own hair (shape, coil width vs an everyday object, shrinkage, wet->dry
 * behavior). The engine does 100% of the curl-type math and returns ONE match.
 *
 * Why this resolves cleanly: each texture is uniquely identified by
 * (coil diameter mm, S/Z pattern). No two share both. So the engine derives a
 * target mm AND a pattern from behavior, and a large pattern penalty makes the
 * S/Z answer decisive WITHIN a coil-size bucket. That kills the old boundary
 * tie that used to dump the user onto a photo-choice screen.
 *
 * Count-agnostic: all product data (name, descriptor, image, price, url, and the
 * coil_size_mm / curl_pattern / hair_type metafields) is read from the
 * #quiz-products-data JSON that sections/quiz-main.liquid renders from
 * collections['all-textures'].products. Nothing here hardcodes a texture name
 * or a count.
 *
 * State lives in sessionStorage so the back button works and a mid-quiz reload
 * restores the same screen and answers.
 */
(function () {
  var ROOT = document.getElementById('adeori-quiz');
  if (!ROOT) return;

  var dataEl = document.getElementById('quiz-products-data');
  var PRODUCTS = [];
  try { PRODUCTS = JSON.parse((dataEl && dataEl.textContent) || '[]'); } catch (e) { PRODUCTS = []; }
  if (!PRODUCTS.length) return; /* empty-state markup already rendered server-side */

  var WIG_BUILDER_URL = '/pages/custom-wig-builder';

  /* ─── Split coil-scored textures vs the straight/blowout branch ───────── */
  function isStraightBranch(p) {
    if (p.hasCoil) return false;
    var ht = (p.hairType || '').toLowerCase();
    if (ht.indexOf('straight') > -1 || ht.indexOf('blowout') > -1) return true;
    var h = (p.handle || '').toLowerCase();
    var n = (p.name || '').toLowerCase();
    return h.indexOf('straight') > -1 || h.indexOf('blowout') > -1 || n.indexOf('straight') > -1;
  }
  function isBlowout(p) {
    var ht = (p.hairType || '').toLowerCase();
    var h = (p.handle || '').toLowerCase();
    return ht.indexOf('blowout') > -1 || h.indexOf('blowout') > -1 || h.indexOf('kinky-straight') > -1;
  }

  var COIL_PRODUCTS = PRODUCTS.filter(function (p) { return p.hasCoil && p.coilMm != null; });
  var STRAIGHT_PRODUCTS = PRODUCTS.filter(isStraightBranch);
  COIL_PRODUCTS.sort(function (a, b) { return b.coilMm - a.coilMm; }); /* loose → tight */

  var mmValues = COIL_PRODUCTS.map(function (p) { return p.coilMm; });
  var mmMax = mmValues.length ? Math.max.apply(null, mmValues) : 10;
  var mmMin = mmValues.length ? Math.min.apply(null, mmValues) : 2;
  var mmRange = (mmMax - mmMin) || 1;

  /* ─── CSS-drawn curl glyphs (no emoji, no photo dependency) ───────────── */
  function glyphCurl(tightness) {
    var paths = [
      'M4 24c6-10 10-10 12-4s6 6 12-4',
      'M4 24c4-10 8-14 6-18s6-4 8 0-2 8 2 10 8-2 8-10',
      'M6 26c2-8-2-10 2-14s-4-8 0-10 6 2 4 6 4 6 2 10-6 6-4 10-6 4-4-2',
      'M6 27c1-4-3-5-1-8s-3-5 0-7-2-5 1-6 3 2 1 5 3 4 0 7 2 4-1 9'
    ];
    return '<svg class="quiz-glyph-curl" viewBox="0 0 32 32" aria-hidden="true"><path d="' + paths[tightness] + '"/></svg>';
  }
  function glyphStraight() {
    return '<svg class="quiz-glyph-curl" viewBox="0 0 32 32" aria-hidden="true"><path d="M8 4v24M16 4v24M24 4v24"/></svg>';
  }
  function glyphZigzag() {
    return '<svg class="quiz-glyph-curl" viewBox="0 0 32 32" aria-hidden="true"><path d="M6 6l8 8-8 8 8 8M18 6l8 8-8 8"/></svg>';
  }
  function glyphCloud() {
    return '<svg class="quiz-glyph-curl" viewBox="0 0 32 32" aria-hidden="true"><path d="M8 22a5 5 0 01-1-9.9A6 6 0 0119 11a4 4 0 015 4 4 4 0 01-4 4z"/></svg>';
  }
  function glyphChalk(w) {
    return '<svg class="quiz-glyph-curl" viewBox="0 0 32 32" aria-hidden="true"><rect x="' + (16 - w / 2) + '" y="6" width="' + w + '" height="20" rx="' + (w / 2) + '"/></svg>';
  }

  /* ─── Question option sets ─────────────────────────────────────────────
     Wording is a first-person OBSERVATION. The scoring dimensions (mm, S/Z)
     are backend-only and never shown to the user. ─────────────────────── */

  /* Q1 - curl behavior with no product = SHAPE (S vs Z) + a coarse size prior.
     pattern: 'S' | 'Z' | null(straight);  prior: coarse mm anchor by name. */
  var Q1_OPTIONS = [
    { value: 'loose',   glyph: glyphCurl(0),   pattern: 'S', prior: 'loose',    label: 'Loose waves and big, open curls', sub: 'Curl hangs open with lots of movement' },
    { value: 'ringlet', glyph: glyphCurl(1),   pattern: 'S', prior: 'mid',      label: 'Springy, defined ringlets or corkscrews', sub: 'A clear spiral shape that holds on its own' },
    { value: 'zigzag',  glyph: glyphZigzag(),  pattern: 'Z', prior: 'tight',    label: 'Tight coils that bend at sharp, zig-zag angles', sub: 'Folds back on itself rather than looping' },
    { value: 'cloud',   glyph: glyphCloud(),   pattern: 'Z', prior: 'tightest', label: 'Dense and cottony, with no clear curl showing', sub: 'Reads as a soft cloud until you twist or coil it' },
    { value: 'straight',glyph: glyphStraight(),pattern: null,prior: 'straight', label: 'Straight or blown out, no curl pattern right now', sub: 'We’ll ask one quick question instead of the rest' }
  ];

  /* Q2 - coil width vs an everyday object = near-direct mm reading. */
  var Q2_OPTIONS = [
    { value: 10, glyph: glyphChalk(14), label: 'About as wide as sidewalk chalk', sub: 'A big, loose loop' },
    { value: 6,  glyph: glyphChalk(9),  label: 'About as wide as a marker', sub: 'A rounded, springy loop' },
    { value: 4,  glyph: glyphChalk(6),  label: 'About as wide as a pencil', sub: 'A tight corkscrew' },
    { value: 3,  glyph: glyphChalk(4),  label: 'About as wide as a drinking straw', sub: 'A small, dense coil' },
    { value: 2,  glyph: glyphChalk(2),  label: 'Tighter than a straw, or too tight to find one loop', sub: 'Very small coils, or no single loop to measure' }
  ];

  /* Q3 - shrinkage magnitude (0 loose → 3 most). Behavioral, measurable. */
  var Q3_OPTIONS = [
    { value: 0, label: 'Barely shrinks', sub: 'Dries close to its wet, stretched length' },
    { value: 1, label: 'Shrinks a little', sub: 'About a quarter to a third shorter' },
    { value: 2, label: 'Shrinks a lot, roughly half', sub: 'Noticeably shorter once dry' },
    { value: 3, label: 'Shrinks dramatically', sub: 'Dries to well under half its stretched length' }
  ];

  /* Q4 - wet→dry definition. 'cloud' is the 4C hallmark (forces tightest + Z). */
  var Q4_OPTIONS = [
    { value: 'defined',  label: 'Clumps into clear curls and keeps them once dry', sub: 'Definition without any product' },
    { value: 'tightens', label: 'Shows curls when wet, then tightens and shrinks a lot as it dries', sub: '' },
    { value: 'cloud',    label: 'Dries into a soft cloud, no clear curls unless I twist or coil it myself', sub: '' }
  ];

  /* Q5 - how they want to wear it. Routing + care tailoring, not the match. */
  var Q5_OPTIONS = [
    { value: 'weft',   label: 'Sew-in or wefts I install myself' },
    { value: 'wig',    label: 'A custom wig built for me' },
    { value: 'unsure', label: 'Not sure yet, show me the match' }
  ];

  /* Coarse mm anchors for Q1 (relative to the live coil-mm spread). */
  var Q1_PRIOR_MM = {
    loose:    mmMax,
    mid:      mmMin + mmRange * 0.55,
    tight:    mmMin + mmRange * 0.2,
    tightest: mmMin
  };

  /* ─── Navigation state ─────────────────────────────────────────────── */
  var SCREENS = ['intro', 'q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'results'];
  var STEP_LABELS = { q1: 'Shape', q2: 'Coil Width', q3: 'Shrinkage', q4: 'Wet to Dry', q5: 'Wear', q6: 'One More' };
  var STORAGE_KEY = 'adeoriQuizState';
  var KLAVIYO_PENDING_KEY = 'adeoriQuizKlaviyoPending';
  var KLAVIYO_COMPANY_ID = 'U4qGkn';
  var lastResult = null;

  var defaultState = function () {
    return {
      screenIdx: 0,
      history: [0],
      answers: { q1: null, q2: null, q3: null, q4: null, q5: null, q6: null },
      route: 'coil'
    };
  };
  var state = defaultState();

  function saveState() {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) {}
  }
  function loadState() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      var parsed = JSON.parse(raw);
      if (parsed && parsed.answers) { state = parsed; return true; }
    } catch (e) {}
    return false;
  }

  var progressBar = document.getElementById('quiz-progress');
  var progressFill = document.getElementById('quiz-progress-fill');
  var stepLabel = document.getElementById('quiz-step-label');
  var backBtn = document.getElementById('quiz-back');

  function screenEl(name) { return document.getElementById('screen-' + name); }

  function totalForRoute() {
    return state.route === 'straight' ? 2 : 5;
  }

  /* Which step number the current screen is, within the active route. */
  function stepNumber(name) {
    if (state.route === 'straight') {
      if (name === 'q1') return 1;
      if (name === 'q6') return 2;
      return 1;
    }
    var order = ['q1', 'q2', 'q3', 'q4', 'q5'];
    var i = order.indexOf(name);
    return i > -1 ? i + 1 : 1;
  }

  function render() {
    var name = SCREENS[state.screenIdx];
    SCREENS.forEach(function (s) {
      var el = screenEl(s);
      if (el) el.classList.remove('is-active', 'slide-back');
    });
    var el = screenEl(name);
    if (!el) return;
    el.classList.add('is-active');

    var showProgress = (name !== 'intro' && name !== 'results');
    progressBar.hidden = !showProgress;
    if (showProgress) {
      var qNum = stepNumber(name);
      var total = totalForRoute();
      stepLabel.innerHTML = 'Question ' + Math.min(qNum, total) + ' of ' + total + ' &middot; <span class="step-name">' + (STEP_LABELS[name] || '') + '</span>';
      var pct = Math.min(100, Math.round((qNum / total) * 100));
      progressFill.style.width = pct + '%';
    }
    backBtn.hidden = state.history.length <= 1;
    ROOT.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function goTo(name) {
    var idx = SCREENS.indexOf(name);
    if (idx === -1) return;
    state.history.push(idx);
    state.screenIdx = idx;
    saveState();
    render();
  }

  function goBack() {
    if (state.history.length <= 1) return;
    state.history.pop();
    state.screenIdx = state.history[state.history.length - 1];
    saveState();
    render();
  }

  backBtn.addEventListener('click', goBack);
  var startBtn = document.getElementById('quiz-start-btn');
  if (startBtn) startBtn.addEventListener('click', function () { goTo('q1'); });

  /* ─── Generic answer-card renderer with keyboard support ──────────────── */
  function renderAnswers(containerId, options, currentValue, onSelect) {
    var container = document.getElementById(containerId);
    if (!container) return;
    container.innerHTML = '';
    options.forEach(function (opt) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'quiz-answer';
      btn.setAttribute('role', 'radio');
      var isSelected = currentValue !== undefined && currentValue !== null && JSON.stringify(currentValue) === JSON.stringify(opt.value);
      btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      btn.classList.toggle('selected', isSelected);
      var glyphHtml = opt.glyph ? '<span class="quiz-answer__glyph">' + opt.glyph + '</span>' : '';
      btn.innerHTML =
        glyphHtml +
        '<span class="quiz-answer__body">' +
          '<span class="quiz-answer__label">' + opt.label + '</span>' +
          (opt.sub ? '<span class="quiz-answer__sub">' + opt.sub + '</span>' : '') +
        '</span>' +
        '<span class="quiz-answer__check"><svg viewBox="0 0 14 14" fill="none"><path d="M2 7l3.5 3.5L12 3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
      btn.addEventListener('click', function () { onSelect(opt.value, btn, container); });
      container.appendChild(btn);
    });
    container.addEventListener('keydown', function (e) {
      var items = Array.prototype.slice.call(container.querySelectorAll('.quiz-answer'));
      var idx = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        if (idx > -1) items[(idx + 1) % items.length].focus();
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        if (idx > -1) items[(idx - 1 + items.length) % items.length].focus();
      } else if ((e.key === 'Enter' || e.key === ' ') && document.activeElement && document.activeElement.classList.contains('quiz-answer')) {
        e.preventDefault();
        document.activeElement.click();
      }
    });
  }

  function markSelected(container, btn) {
    Array.prototype.forEach.call(container.querySelectorAll('.quiz-answer'), function (c) {
      c.classList.remove('selected');
      c.setAttribute('aria-checked', 'false');
    });
    btn.classList.add('selected');
    btn.setAttribute('aria-checked', 'true');
  }

  function optByValue(options, val) {
    for (var i = 0; i < options.length; i++) {
      if (JSON.stringify(options[i].value) === JSON.stringify(val)) return options[i];
    }
    return null;
  }

  /* Normalize a curl_pattern metafield ("s-pattern" / "z-pattern") to S | Z.
     Straight-branch values ("kinky straight...", "straight...") return null. */
  function patCode(p) {
    if (!p || !p.curlPattern) return null;
    var c = p.curlPattern.trim().charAt(0).toUpperCase();
    return (c === 'S' || c === 'Z') ? c : null;
  }

  /* ─── Question renderers (idempotent - safe to call on restore) ───────── */
  function bindQ1() {
    renderAnswers('q1-answers', Q1_OPTIONS, state.answers.q1, function (val, btn, container) {
      markSelected(container, btn);
      state.answers.q1 = val;
      saveState();
      setTimeout(function () {
        if (val === 'straight') {
          state.route = 'straight';
          saveState();
          bindStraightBranch();
          goTo('q6');
        } else {
          state.route = 'coil';
          saveState();
          goTo('q2');
        }
      }, 160);
    });
  }
  function bindQ2() {
    renderAnswers('q2-answers', Q2_OPTIONS, state.answers.q2, function (val, btn, container) {
      markSelected(container, btn);
      state.answers.q2 = val;
      saveState();
      setTimeout(function () { goTo('q3'); }, 160);
    });
  }
  function bindQ3() {
    renderAnswers('q3-answers', Q3_OPTIONS, state.answers.q3, function (val, btn, container) {
      markSelected(container, btn);
      state.answers.q3 = val;
      saveState();
      setTimeout(function () { goTo('q4'); }, 160);
    });
  }
  function bindQ4() {
    renderAnswers('q4-answers', Q4_OPTIONS, state.answers.q4, function (val, btn, container) {
      markSelected(container, btn);
      state.answers.q4 = val;
      saveState();
      setTimeout(function () { goTo('q5'); }, 160);
    });
  }
  function bindQ5() {
    renderAnswers('q5-answers', Q5_OPTIONS, state.answers.q5, function (val, btn, container) {
      markSelected(container, btn);
      state.answers.q5 = val;
      saveState();
      setTimeout(function () { finalizeCoilRoute(); }, 160);
    });
  }

  /* ─── Scoring: coil route ───────────────────────────────────────────────
     targetMm = coil-width answer (Q2, near-direct) blended with the Q1 prior
     and the Q3 shrinkage cue. preferPattern = Q1 shape, forced to Z + tightest
     when Q4 says "dries to a cloud" (the 4C hallmark). Pattern is decisive
     WITHIN a size bucket via a large penalty, so same-mm S/Z pairs resolve
     deterministically - no photo tiebreak. ────────────────────────────── */
  function derivePattern() {
    if (state.answers.q4 === 'cloud') return 'Z';
    var o = optByValue(Q1_OPTIONS, state.answers.q1);
    return o ? o.pattern : null;
  }

  function estimateMm() {
    var samples = [];
    var weights = [];

    /* Q2 (coil width vs an everyday object) is a near-direct diameter reading -
       the validated primary signal. It dominates; the others only nudge. */
    if (state.answers.q2 != null) { samples.push(state.answers.q2); weights.push(6); }

    var o1 = optByValue(Q1_OPTIONS, state.answers.q1);
    if (o1 && Q1_PRIOR_MM[o1.prior] != null) { samples.push(Q1_PRIOR_MM[o1.prior]); weights.push(0.5); }

    if (state.answers.q3 != null) { samples.push(mmMax - (state.answers.q3 / 3) * mmRange); weights.push(1); }

    /* "dries to a cloud with no visible curl" is the 4C hallmark - pull hard
       toward the tightest coil. Other Q4 answers describe definition, not
       diameter, so they don't move the mm estimate. */
    if (state.answers.q4 === 'cloud') { samples.push(mmMin); weights.push(3); }

    if (!samples.length) return (mmMax + mmMin) / 2;
    var num = 0, den = 0;
    for (var i = 0; i < samples.length; i++) { num += samples[i] * weights[i]; den += weights[i]; }
    return num / den;
  }

  function closestCoilProducts(targetMm, preferPattern) {
    /* Pattern penalty is a full coil-range wide: within any size bucket the
       S/Z answer decides, but it never overrides a clearly different size. */
    var patternPenalty = mmRange * 1.0;
    var scored = COIL_PRODUCTS.map(function (p) {
      var dist = Math.abs(p.coilMm - targetMm);
      var pc = patCode(p);
      var mismatch = (preferPattern && pc && pc !== preferPattern);
      return { product: p, dist: dist + (mismatch ? patternPenalty : 0), rawDist: dist };
    });
    scored.sort(function (a, b) {
      if (a.dist !== b.dist) return a.dist - b.dist;
      /* deterministic fallback: tighter coil wins ties, never a coin flip */
      return a.product.coilMm - b.product.coilMm;
    });
    return scored;
  }

  var pendingResult = null;

  function finalizeCoilRoute() {
    var targetMm = estimateMm();
    var preferPattern = derivePattern();
    var scored = closestCoilProducts(targetMm, preferPattern);
    if (!scored.length) { renderEmptyResult(); return; }
    var best = scored[0];
    var second = scored[1];
    /* Show the runner-up softly only when the second pick is genuinely close
       on raw coil distance (same/adjacent size). It is a suggestion, never a
       decision handed back to the customer. */
    var close = !!(second && Math.abs(second.rawDist - best.rawDist) <= Math.max(0.6, mmRange * 0.18));
    pendingResult = {
      primary: best.product,
      runnerUp: second ? second.product : null,
      targetMm: targetMm,
      pattern: preferPattern,
      close: close
    };
    showResults(pendingResult);
    goTo('results');
  }

  /* ─── Q6 - straight/blowout branch (Q1 = straight). A behavioral question
     about how they wear it, NOT a texture self-diagnosis. ──────────────── */
  function bindStraightBranch() {
    document.getElementById('q6-heading').textContent = 'Do you usually wear a fresh blowout, or is this your natural straight base?';
    document.getElementById('q6-hint').textContent = 'Two quick options and you’re done.';
    var blowout = STRAIGHT_PRODUCTS.filter(isBlowout)[0];
    var natural = STRAIGHT_PRODUCTS.filter(function (p) { return p !== blowout; })[0];

    if (!blowout || !natural) {
      var only = STRAIGHT_PRODUCTS[0];
      if (!only) { renderEmptyResult(); return; }
      pendingResult = { primary: only, runnerUp: null, close: false, isStraightRoute: true };
      showResults(pendingResult);
      goTo('results');
      return;
    }

    var options = [
      { value: 'blowout', label: 'A fresh blowout or silk-press finish', sub: 'Body and movement, straightened from natural' },
      { value: 'natural', label: 'A sleek, natural straight base', sub: 'The smooth base under a silk press' }
    ];
    renderAnswers('q6-answers', options, state.answers.q6, function (val, btn, container) {
      markSelected(container, btn);
      state.answers.q6 = val;
      saveState();
      var chosen = val === 'blowout' ? blowout : natural;
      var other = val === 'blowout' ? natural : blowout;
      pendingResult = { primary: chosen, runnerUp: other, close: false, isStraightRoute: true };
      setTimeout(function () { showResults(pendingResult); goTo('results'); }, 160);
    });
  }

  function renderEmptyResult() {
    var h1 = document.getElementById('results-h1');
    if (h1) h1.textContent = 'We couldn’t find a match just yet';
    goTo('results');
  }

  /* ─── Why-line - composed only from the user's own answers (show-your-work) ─── */
  function buildWhyLine(product, ctx) {
    if (ctx.isStraightRoute) {
      var lookNote = state.answers.q6 === 'blowout' ? 'a fresh blowout or silk-press finish' : 'a sleek, natural straight base';
      return 'You told us you wear ' + lookNote + ', and that’s exactly the look ' + product.name + (product.descriptor ? ', ' + product.descriptor + ',' : '') + ' is made for, in 100% cuticle-aligned human hair.';
    }
    /* Each shape carries the pattern it implies, so we never echo an S-shape
       word ("ringlets") for a Z-coil match. */
    var shapeLabels = {
      loose:   { txt: 'your hair falls into loose, open curls', pat: 'S' },
      ringlet: { txt: 'your hair forms springy, defined ringlets', pat: 'S' },
      zigzag:  { txt: 'your hair coils into tight, zig-zag angles', pat: 'Z' },
      cloud:   { txt: 'your hair dries into a dense, cottony cloud', pat: 'Z' }
    };
    var q3Labels = { 0: 'barely shrinks as it dries', 1: 'shrinks a little as it dries', 2: 'shrinks by about half', 3: 'shrinks dramatically once dry' };
    var q4Labels = {
      defined: 'holds its curl without product',
      tightens: 'tightens and shrinks as it dries',
      cloud: 'dries into a soft cloud until you coil it yourself'
    };
    var pc = patCode(product);
    var parts = [];
    /* Lead with the defining behavior. If they said "dries to a cloud" (the 4C
       hallmark that overrode to a Z coil), describe THAT, not the Q1 shape - and
       otherwise only state the Q1 shape when it agrees with the matched pattern. */
    if (state.answers.q4 === 'cloud') {
      parts.push('your hair dries into a dense, cottony cloud until you coil it yourself');
    } else {
      var sp = shapeLabels[state.answers.q1];
      if (sp && (!pc || sp.pat === pc)) parts.push(sp.txt);
    }
    if (state.answers.q3 != null && q3Labels[state.answers.q3]) parts.push('it ' + q3Labels[state.answers.q3]);
    else if (state.answers.q4 && state.answers.q4 !== 'cloud' && q4Labels[state.answers.q4]) parts.push('it ' + q4Labels[state.answers.q4]);
    if (!parts.length) parts.push('your curl behaves the way ' + product.name + ' does');
    return 'You told us ' + parts.join(', and ') + '. That’s the signature of ' + product.name + (product.descriptor ? ', ' + product.descriptor : '') + ', steam-set to move exactly like yours.';
  }

  function chipsFor(product) {
    var chips = [];
    if (product.hairType) chips.push(product.hairType);
    if (product.hasCoil && patCode(product)) chips.push(patCode(product) === 'Z' ? 'Z-pattern coil' : 'S-pattern curl');
    if (product.hasCoil && product.coilMm != null) chips.push(product.coilMm + 'mm coil');
    return chips;
  }

  function showResults(result) {
    var product = result.primary;
    if (!product) { renderEmptyResult(); return; }
    lastResult = result;

    document.getElementById('results-h1').innerHTML = 'Your match: <em>' + product.name + '</em>';
    var img = document.getElementById('results-img');
    img.src = product.image;
    img.alt = product.title || product.name || 'Your matched Adéorí texture';
    document.getElementById('results-name').textContent = product.name;
    document.getElementById('results-descriptor').textContent = product.descriptor || '';

    var chipsWrap = document.getElementById('results-chips');
    chipsWrap.innerHTML = '';
    chipsFor(product).forEach(function (c) {
      var span = document.createElement('span');
      span.className = 'quiz-results__chip';
      span.textContent = c;
      chipsWrap.appendChild(span);
    });

    document.getElementById('results-why').textContent = buildWhyLine(product, result);
    document.getElementById('results-price').textContent = product.priceFormatted;

    /* CTA routes to the wig builder when they said they want a custom wig. */
    var cta = document.getElementById('results-cta');
    if (state.answers.q5 === 'wig') {
      cta.href = WIG_BUILDER_URL + '?texture=' + encodeURIComponent(product.handle) + '#wb-builder';
      cta.textContent = 'Build my wig in ' + product.name + ' →';
    } else {
      cta.href = product.url;
      cta.textContent = 'Shop ' + product.name + ' →';
    }

    var runnerWrap = document.getElementById('results-runner');
    if (result.runnerUp && result.close) {
      var ru = result.runnerUp;
      document.getElementById('runner-img').src = ru.image;
      document.getElementById('runner-img').alt = ru.title || ru.name;
      document.getElementById('runner-name').textContent = ru.name;
      var diffNote = 'A close second, one step ' + (((ru.coilMm || 0) > (product.coilMm || 0)) ? 'looser' : 'tighter') + ', if you want to compare.';
      document.getElementById('runner-note').textContent = diffNote;
      document.getElementById('runner-cta').href = ru.url;
      runnerWrap.hidden = false;
    } else if (result.runnerUp && result.isStraightRoute) {
      var ru2 = result.runnerUp;
      document.getElementById('runner-img').src = ru2.image;
      document.getElementById('runner-img').alt = ru2.title || ru2.name;
      document.getElementById('runner-name').textContent = ru2.name;
      document.getElementById('runner-note').textContent = 'The other straight option, if this isn’t quite it.';
      document.getElementById('runner-cta').href = ru2.url;
      runnerWrap.hidden = false;
    } else {
      runnerWrap.hidden = true;
    }

    var tagField = document.getElementById('quiz-email-texture-tag');
    if (tagField) tagField.value = 'quiz-match-' + product.handle;

    try {
      var encoded = btoa(JSON.stringify({ h: product.handle, r: result.runnerUp ? result.runnerUp.handle : null }));
      history.replaceState(null, '', window.location.pathname + '?match=' + encoded);
    } catch (e) {}
  }

  /* ─── Retake ────────────────────────────────────────────────────────── */
  var retakeBtn = document.getElementById('retake-quiz-btn');
  if (retakeBtn) {
    retakeBtn.addEventListener('click', function () {
      state = defaultState();
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
      Array.prototype.forEach.call(document.querySelectorAll('.quiz-answer.selected'), function (el) {
        el.classList.remove('selected');
        el.setAttribute('aria-checked', 'false');
      });
      bindQ1();
      try { history.replaceState(null, '', window.location.pathname); } catch (e) {}
      goTo('intro');
      state.history = [0];
      saveState();
    });
  }

  /* ─── Q1 help toggle (strand-test tip) ──────────────────────────────── */
  (function setupQ1Help() {
    var q1Screen = screenEl('q1');
    if (!q1Screen) return;
    var wrap = document.createElement('div');
    wrap.className = 'quiz-help';
    wrap.innerHTML =
      '<button type="button" class="quiz-help__toggle" id="q1-help-toggle">Not sure? Try the strand test →</button>' +
      '<div class="quiz-help__tip" id="q1-help-tip">Wash a small section, add nothing, and let it air-dry flat. Look at the mid-shaft, not the roots or ends. That’s your truest pattern. A smooth loop reads as an “S”; a sharp fold reads as a “Z”.</div>';
    q1Screen.querySelector('.quiz-question').appendChild(wrap);
    document.getElementById('q1-help-toggle').addEventListener('click', function () {
      var tip = document.getElementById('q1-help-tip');
      tip.classList.toggle('is-open');
      this.textContent = tip.classList.contains('is-open') ? 'Hide ↑' : 'Not sure? Try the strand test →';
    });
  })();

  /* ─── Klaviyo onsite bridge ─────────────────────────────────────────── */
  function ensureKlaviyo() {
    if (!window.klaviyo) {
      window._klOnsite = window._klOnsite || [];
      try {
        window.klaviyo = new Proxy({}, {
          get: function (target, prop) {
            return prop === 'push'
              ? function () { window._klOnsite.push.apply(window._klOnsite, arguments); }
              : function () {
                  var args = Array.prototype.slice.call(arguments);
                  var cb = typeof args[args.length - 1] === 'function' ? args.pop() : undefined;
                  return new Promise(function (resolve) {
                    window._klOnsite.push([prop].concat(args, [function (res) {
                      if (cb) cb(res);
                      resolve(res);
                    }]));
                  });
                };
          }
        });
      } catch (e) {
        window.klaviyo = window.klaviyo || [];
        window.klaviyo.push = function () { window._klOnsite.push.apply(window._klOnsite, arguments); };
      }
    }
    if (!document.querySelector('script[src*="static.klaviyo.com/onsite/js"]')) {
      var s = document.createElement('script');
      s.async = true;
      s.src = 'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=' + KLAVIYO_COMPANY_ID;
      document.head.appendChild(s);
    }
  }

  function flushKlaviyoPending() {
    var raw = null;
    try { raw = sessionStorage.getItem(KLAVIYO_PENDING_KEY); } catch (e) {}
    if (!raw) return;
    if (!document.getElementById('quiz-email-sent')) return;
    try { sessionStorage.removeItem(KLAVIYO_PENDING_KEY); } catch (e) {}
    var pending = null;
    try { pending = JSON.parse(raw); } catch (e) {}
    if (!pending || !pending.email || !pending.props) return;
    ensureKlaviyo();
    var tracked = false;
    var doTrack = function () {
      if (tracked) return;
      tracked = true;
      window.klaviyo.push(['track', 'Quiz Completed', pending.props]);
    };
    window.klaviyo.push(['identify', { email: pending.email }, doTrack]);
    window.setTimeout(doTrack, 4000);
  }

  /* ─── Optional email-my-match ───────────────────────────────────────── */
  (function setupEmailSave() {
    var formEl = document.getElementById('quiz-email-form');
    if (!formEl) return;
    formEl.addEventListener('submit', function (e) {
      e.preventDefault();
      var emailInput = document.getElementById('quiz-email-input');
      var email = emailInput ? emailInput.value.replace(/^\s+|\s+$/g, '') : '';
      if (!email || (emailInput && emailInput.checkValidity && !emailInput.checkValidity())) {
        if (emailInput) emailInput.focus();
        return;
      }
      var wrap = document.getElementById('quiz-email-save');

      // Build the Quiz Completed event props from the match result.
      var props = { 'discount_code': 'ADEORI10' };
      if (lastResult && lastResult.primary) {
        var origin = window.location.origin;
        var abs = function (u) {
          if (!u) return u;
          if (u.indexOf('//') === 0) return 'https:' + u;
          if (u.charAt(0) === '/') return origin + u;
          return u;
        };
        var lockup = function (p) { return p.name + (p.descriptor ? ' - ' + p.descriptor : ''); };
        var p = lastResult.primary;
        props['Recommended Product'] = lockup(p);
        props['Recommended Product URL'] = abs(p.url);
        props['Recommended Product Handle'] = p.handle;
        props['Quiz Route'] = state.route;
        props['Wear Preference'] = state.answers.q5 || '';
        if (p.image) props['Recommended Product Image'] = abs(p.image);
        if (p.coilMm != null) props['Coil Size mm'] = p.coilMm;
        if (p.curlPattern) props['Curl Pattern'] = p.curlPattern;
        if (p.hairType) props['Hair Type'] = p.hairType;
        if (lastResult.runnerUp) {
          props['Runner Up'] = lockup(lastResult.runnerUp);
          props['Runner Up URL'] = abs(lastResult.runnerUp.url);
        }
      }

      // Klaviyo client API: subscribe to the list + track "Quiz Completed" (drives the match email).
      var kfetch = function (path, body) {
        return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + KLAVIYO_COMPANY_ID, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
          body: JSON.stringify(body)
        }).catch(function () {});
      };
      kfetch('subscriptions', { data: { type: 'subscription', attributes: { profile: { data: { type: 'profile', attributes: { email: email, properties: { source: 'quiz', 'Quiz Match': props['Recommended Product'] || '' } } } } }, relationships: { list: { data: { type: 'list', id: 'Wkjy5k' } } } } });
      kfetch('events', { data: { type: 'event', attributes: { metric: { data: { type: 'metric', attributes: { name: 'Quiz Completed' } } }, properties: props, profile: { data: { type: 'profile', attributes: { email: email } } } } } });

      // 3) Inline success state (no page navigation).
      if (wrap) {
        wrap.classList.add('is-submitted');
        wrap.innerHTML = '<div class="quiz-email-save__title" id="quiz-email-sent">Sent.</div>' +
          '<p class="quiz-email-save__sub">Check your inbox for your match and the care guide.</p>';
      }
    });
  })();

  /* ─── Boot: restore prior session state if present, else start fresh ──── */
  function boot() {
    flushKlaviyoPending();
    bindQ1();
    bindQ2();
    bindQ3();
    bindQ4();
    bindQ5();

    if (loadState()) {
      if (state.answers.q1 === 'straight') {
        bindStraightBranch();
      }
      /* If we'd already reached results, recompute rather than trusting any
         stale stored product reference. */
      if (SCREENS[state.screenIdx] === 'results') {
        if (state.route === 'straight') {
          bindStraightBranch();
        } else if (state.answers.q5 !== null) {
          finalizeCoilRoute();
          return;
        }
      }
    }
    render();
  }

  boot();
})();
